import React, { useEffect, useState } from 'react'
import { Box, Button, TextField, Typography } from '@material-ui/core'
import { useVoiceAgent } from '../../../hooks/useVoiceAgent'
import { useUser } from '../../../hooks/useUser'
import { VoiceAgentSetupDataPatch } from '../../../models/VoiceAgent'
import { CommunitySettings } from '../../../main/containers/settings/PropertyDetails/CommunitySettings'
import {
    EDITABLE_FIELDS,
    EditableFieldKey,
    FieldDef,
    OPTIONAL_FIELDS,
    fieldsFromSetupData,
} from '../constants'
import { useVoiceAgentStyles } from '../useVoiceAgentStyles'
import { usePhoneStyles } from '../../Phone/usePhoneStyles'
import { DocumentsSection } from './DocumentsSection'
import { LeasingSeasonNotice } from './LeasingSeasonNotice'

export const KnowledgePanel: React.FC = () => {
    const classes = useVoiceAgentStyles()
    const phone = usePhoneStyles()
    const { workspaceUser } = useUser()
    const apartmentId = workspaceUser?.active_workspace?.id
    const { setupData, saveSetupData } = useVoiceAgent()
    const [fields, setFields] = useState<Record<EditableFieldKey, string>>(
        {} as Record<EditableFieldKey, string>,
    )

    useEffect(() => {
        if (!setupData) return
        setFields(fieldsFromSetupData(setupData))
    }, [setupData])

    const handleFieldChange = (key: EditableFieldKey, value: string) => {
        setFields((prev) => ({ ...prev, [key]: value }))
    }

    const renderField = (field: FieldDef) => {
        const { key, label, placeholder, multiline, helper } = field
        return (
            <Box
                key={key}
                className={classes.fieldGroup}
                style={multiline ? { gridColumn: '1 / -1' } : undefined}
            >
                <TextField
                    label={label}
                    placeholder={placeholder}
                    value={fields[key] ?? ''}
                    onChange={(e) => handleFieldChange(key, e.target.value)}
                    fullWidth
                    variant="outlined"
                    size="small"
                    multiline={multiline}
                    rows={multiline ? 3 : undefined}
                    InputLabelProps={{ shrink: true }}
                    helperText={helper}
                />
            </Box>
        )
    }

    const handleSave = async () => {
        const patch: VoiceAgentSetupDataPatch = {}
        EDITABLE_FIELDS.forEach(({ key }) => {
            patch[key] = fields[key] ?? ''
        })
        try {
            await saveSetupData.mutateAsync(patch)
        } catch {
            // toast handled by hook
        }
    }

    return (
        <Box className={classes.knowledgeSplit}>
            {apartmentId != null && (
                <Box className={phone.card} p={3}>
                    <CommunitySettings apartmentId={apartmentId} embedded />
                </Box>
            )}

            <Box className={classes.knowledgeSplitSide}>
                <Box className={phone.card} p={3}>
                    <Typography className={classes.fieldSectionTitle}>
                        Agent phrasing
                    </Typography>
                    <Typography className={classes.fieldSectionDesc}>
                        These lines are only for how the agent talks. Property
                        facts on the left come from Community.
                    </Typography>
                    <LeasingSeasonNotice />
                    <Box className={classes.fieldGrid}>
                        {OPTIONAL_FIELDS.map(renderField)}
                    </Box>
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.btnPrimary}
                        onClick={handleSave}
                        disabled={saveSetupData.isLoading}
                        style={{ marginTop: 8 }}
                    >
                        {saveSetupData.isLoading
                            ? 'Saving...'
                            : 'Save agent phrasing'}
                    </Button>
                </Box>

                <Box className={phone.card} p={3}>
                    <DocumentsSection
                        totalChars={setupData?.documents?.total_chars ?? 0}
                        docCount={setupData?.documents?.count ?? 0}
                    />
                </Box>
            </Box>
        </Box>
    )
}
